import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import Button, { HEIGHT } from 'design-system/Button';
import Indicator from 'design-system/Indicator';
import { AppView } from 'design-system/AppView';
import ListEmptyView from 'design-system/ListEmptyView';
import StackHeader from 'design-system/hoc/StackHeader';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { getTransactionsFlatlistData, } from 'utils/getTransactions';
import Amplitude from 'utils/amplitude';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useSyncMonthlyExpenses from 'features/analytics/hooks/useSyncMonthlyExpenses';
import TransactionsHeader from 'features/transactions/components/TransactionsHeader';
import useAdvancedEditTransactions from 'features/transactions/hooks/useAdvancedEditTransactions';
import useNavigateTransactionItem from 'features/transactions/hooks/useNavigateTransactionItem';
import { selectBudgetingPosition } from 'features/analytics/selectors';
import { setBudgetingPosition } from 'actions';
import OpenInvestAccountCTA from 'features/pots/components/OpenInvestAccountCTA';
import { Spacer } from 'design-system';
import { selectMerchantBudgets, makeSelectCategoryTotalById, makeSelectMerchantTotalById, selectBudgets, } from '../selectors';
import { getBudgets } from '../actions';
import { isWeb } from '../../../constants';
import CategoryEditModal from '../components/CategoryEditModal';
import { getMerchantBudgetById } from '../utils/getMerchantBudget';
import MonthlyCategoryHeader from '../components/MonthlyCategoryHeader';
import MonthlyCategoryTransactionRow from '../components/MonthlyCategoryTransactionRow';
import { useEditBudgetsCategoryModal } from '../hooks/useEditBudgetsCategoryModal';
import { useBudgetingNavigate } from '../hooks/useBudgetingNavigate';
const keyExtractor = (item) => item.id.toString();
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const MonthlyCategoryScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const navRightBtnRef = useRef(null);
    const styles = useStyles(styleSet);
    useEffect(() => () => {
        if (typeof route.params?.resetPosition === 'number') {
            dispatch(setBudgetingPosition(route.params.resetPosition));
        }
    }, [route.params?.resetPosition]);
    const navigateToTransactionItem = useNavigateTransactionItem();
    const data = useMemo(() => {
        if (route.params?.to &&
            route.params?.from &&
            (route.params?.merchantId || route.params?.categoryId)) {
            return {
                step: route.params?.step,
                monthEndDate: route.params.to,
                monthStartDate: route.params.from,
                fromCurrentMonth: route.params.from,
                merchantId: route.params?.merchantId,
                categoryId: route.params?.categoryId,
                runOnMount: route.params?.runOnMount,
            };
        }
        return undefined;
    }, [route.params]);
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(getBudgets());
    }, []);
    const [categoryExpenses, isFetchingCategoryExpenses] = useSyncMonthlyExpenses(data);
    const selectCategoryTotalById = useMemo(makeSelectCategoryTotalById, []);
    const selectMerchantTotalById = useMemo(makeSelectMerchantTotalById, []);
    const category = useAppSelector((store) => {
        if (data?.categoryId) {
            return selectCategoryTotalById?.(store, data.categoryId);
        }
        if (data?.merchantId) {
            return selectMerchantTotalById?.(store, data.merchantId);
        }
        return undefined;
    });
    const { onNavigate } = useBudgetingNavigate();
    useEffect(() => {
        if (!category && isWeb) {
            navigation.popToTop();
            onNavigate();
        }
    }, [category, onNavigate]);
    const budgets = useAppSelector(selectBudgets);
    const currentBudgetItem = useMemo(() => budgets.find((budget) => budget.key === `category.${category?.id}`), [budgets, category?.id]);
    const showEditModal = useEditBudgetsCategoryModal({
        onEdit: () => navigation.navigateDeprecated('Budgets', {
            categoryId: data.categoryId,
            supressEditModal: true,
        }),
        category: currentBudgetItem,
    });
    const merchantBudgets = useAppSelector(selectMerchantBudgets);
    const handleEditCategory = useCallback(() => {
        if (data?.categoryId) {
            if (currentBudgetItem?.rollingAccumulatedLimit) {
                showEditModal();
            }
            else {
                navigation.navigateDeprecated('Budgets', {
                    categoryId: data.categoryId,
                });
            }
        }
        else if (data?.merchantId) {
            // For merchants we get the latest merchant budget data, as it may have changed if this is a past period
            const merchantBudget = getMerchantBudgetById(merchantBudgets, data.merchantId);
            const monthlyMerchant = category;
            navigation.navigateDeprecated('EditMerchantBudget', {
                id: data.merchantId ?? monthlyMerchant?.displayName,
                displayName: merchantBudget?.displayName ?? monthlyMerchant?.displayName,
                budgetLimit: merchantBudget?.totalLimit ?? monthlyMerchant.budgetLimit,
                currency: merchantBudget?.currency ?? monthlyMerchant.currency,
                iconUrl: monthlyMerchant.iconUrl,
            });
        }
    }, [
        category,
        currentBudgetItem?.rollingAccumulatedLimit,
        data,
        merchantBudgets,
        showEditModal,
    ]);
    const { selected, isEditing, selectedObj, onEdit, onSelected, openEditModal, } = useAdvancedEditTransactions(navigation);
    const flashListData = useMemo(() => isFetchingCategoryExpenses
        ? []
        : getTransactionsFlatlistData(categoryExpenses, false, true, true), [categoryExpenses, isFetchingCategoryExpenses]);
    const type = data?.categoryId ? 'category' : 'merchant';
    const position = useAppSelector(selectBudgetingPosition);
    const isEditable = type === 'merchant' ? position === 0 : category?.baseLimit != null;
    const onPressEditButton = useCallback(() => {
        if (isEditing) {
            onEdit(false);
            return;
        }
        if (isEditable) {
            if (flashListData.length === 0) {
                handleEditCategory();
            }
            else {
                CategoryEditModal.show(handleEditCategory, onEdit, navRightBtnRef, category?.displayName);
            }
        }
        else {
            onEdit(true);
        }
    }, [
        category?.displayName,
        flashListData.length,
        handleEditCategory,
        isEditing,
        onEdit,
        isEditable,
    ]);
    useLayoutEffect(() => {
        if (category) {
            navigation.setOptions({
                headerRight: isEditable
                    ? () => (<View ref={navRightBtnRef}>
                  <TransactionsHeader isEditing={isEditing} onPressEdit={onPressEditButton}/>
                </View>)
                    : undefined,
                headerTitle: () => <StackHeader title={category?.displayName}/>,
                title: category.displayName,
            });
        }
    }, [category, isEditing, onPressEditButton, isEditable]);
    const onCompare = useCallback((routeWithParams) => {
        navigation.navigateDeprecated(routeWithParams);
        Amplitude.logEvent('Category.openCompare', {
            type: routeWithParams.name,
        });
    }, []);
    const openItem = useCallback((data) => {
        const { isPending } = data;
        if (isEditing) {
            if (isPending)
                return;
            onSelected(data);
            return;
        }
        navigateToTransactionItem(data);
        Amplitude.logEvent('Category.OpenTransaction');
    }, [isEditing, navigateToTransactionItem, onSelected]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', isEditing ? HEIGHT + rem(16) : 0, paddingHorizontalStyle);
    const ListHeaderComponent = useMemo(() => category &&
        data?.monthStartDate &&
        data?.monthEndDate &&
        (data?.categoryId || data?.merchantId) ? (<MonthlyCategoryHeader data={category} periodStartDate={data.monthStartDate} periodEndDate={data.monthEndDate} onPressSetup={handleEditCategory} type={type}/>) : null, [
        category,
        data?.categoryId,
        data?.merchantId,
        data?.monthEndDate,
        data?.monthStartDate,
        handleEditCategory,
        type,
    ]);
    const renderItem = useCallback(({ item }) => (<MonthlyCategoryTransactionRow item={item} onPress={openItem} onCompare={onCompare} guessesHomeCountry={guessedHomeCountry} isEditingTransactions={isEditing} selected={!!selectedObj[item.id]}/>), [guessedHomeCountry, isEditing, onCompare, openItem, selectedObj]);
    const ListEmptyComponent = useMemo(() => {
        if (isFetchingCategoryExpenses) {
            return (<>
            <Spacer h={16}/>
            <Indicator dark/>
          </>);
        }
        return (<ListEmptyView happy onCard compact style={styles.empty} title="There are no transactions"/>);
    }, [isFetchingCategoryExpenses, styles.empty]);
    const hasEditTransaction = isEditing && selected.length !== 0;
    const extraData = useMemo(() => [isEditing, selectedObj], [isEditing, selectedObj]);
    const renderListFooter = useMemo(() => {
        if (route.params?.categoryId === 'investments') {
            return <OpenInvestAccountCTA withTopSpacing/>;
        }
        return null;
    }, [route.params?.categoryId]);
    const baseProps = {
        data: flashListData,
        renderItem,
        scrollEventThrottle: 16,
        onScroll: scrollHandler,
        onEndReachedThreshold: 0.8,
        keyExtractor,
        ListEmptyComponent,
        ListHeaderComponent,
        contentContainerStyle,
        showsVerticalScrollIndicator: true,
        scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
        extraData,
        ListFooterComponent: renderListFooter,
    };
    return (<AppView style={styles.content} withFrame={false}>
        {isWeb ? (<Animated.FlatList {...baseProps}/>) : (<AnimatedFlashList {...baseProps}/>)}

        {hasEditTransaction ? (<Button brand floating title={`Edit (${selected.length}) transactions`} onPress={openEditModal}/>) : null}
      </AppView>);
}, ({ colors }) => ({
    hasCustomTitle: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets(() => ({
    content: {
        flex: 1,
    },
    empty: {
        marginTop: rem(16),
    },
}));
export default MonthlyCategoryScreen;
